import { useCallback, useMemo, useReducer } from 'react'
import invariant from 'tiny-invariant'

const EMPTY_OBJ = Object.freeze( {} )

const ACTIONS = {
  onChange : 'useForm.onChange',
  onSubmit : 'useForm.onSubmit'
}

export default function useForm( opts ) {
  const { fields, onSubmit : pOnSubmit, onChange : pOnChange } = opts || EMPTY_OBJ

  invariant( typeof fields === 'object' && Object.keys( fields ).length > 0, 'No fields supplied to useForm hook' )

  const reducer = useCallback( ( state, action ) => {
    const { type, value, field, errors } = action

    if ( type === ACTIONS.onChange ) {
      const nState = {
        ...state,
        values : {
          ...state.values,
          [field] : value
        },
        errors,
        touches : {
          ...state.touches,
          [field] : true
        }
      }

      return nState
    }

    if ( type === ACTIONS.onSubmit ) {
      const nState = {
        ...state,
        errors,
        touches : Object
          .keys( state.touches )
          .reduce( ( prev, curr ) => {
            prev[curr] = true
            return prev
          }, {} )
      }

      return nState
    }
  
    return state
  }, [] )

  const initialState = useMemo( () => {
    return Object.keys( fields ).reduce( ( prev, curr ) => {
      const field = fields[curr]

      prev.errors[curr] = null
      prev.values[curr] = field.initialValue || null
      prev.touches[curr] = field.initialValue != null || field.optional

      return prev
    }, {
      errors : {},
      values : {},
      touches : {},
      formErrors : null,
      validating : false
    } )
  }, [ fields ] )

  const [ state, dispatch ] = useReducer( reducer, initialState )

  const onChange = useCallback( ( { value, data } ) => {
    const nValues = {
      ...state.values,
      [data] : value
    }

    const errors = getFormErrors( fields, nValues, state.touches, data )

    dispatch( {
      type : ACTIONS.onChange,
      value,
      errors,
      field : data
    } )

    pOnChange?.( { value, data }, errors )
  }, [ fields, state.values, state.touches, dispatch, pOnChange ] )

  return useMemo( () => {

    return {
      ...state,
      onChange,
      onSubmit : ( { data } ) => {
        const errors = getFormErrors( fields, state.values, state.touches )

        if ( Object.keys( errors ).length === 0 ) {
          pOnSubmit?.( {
            ...state.values,
            data
          } )
        } else {
          dispatch( {
            type : ACTIONS.onSubmit,
            errors
          } )
        }
      }
    }
  }, [ state, pOnSubmit, onChange, fields ] )
}

function getFormErrors( fields, values, touches, currentField ) {
  const errors = Object
    .keys( fields )
    .reduce( ( prev, field ) => {
      // validate if we aren't changing a particular field or if the field has been touched or is the current field
      if ( !currentField || ( touches[field] || field === currentField ) ) {
        const value = values[field]
        const f = fields[field]
        const validationResult = f?.validator ? f?.validator?.( value, values ) : true

        const error = typeof validationResult === 'boolean' ?
          validationResult ?
            null :
            f?.errorMessage?.( value, field, values ) || `${field} is invalid` :
          validationResult

        if ( error ) {
          prev[field] = error ?
            Array.isArray( error ) ?
              error :
              [ error ] :
            null
        }
      }

      return prev
    }, {} )
      
  return errors
}
